<template>
    <div>
        <dropdown v-if="displayAsDropdown" v-model="isActive" ref="dropdown" :disabled="disabled" :disable-hide="persistent" plain-menu persistent-on-same :left="left" :top="top" :bottom="bottom" :right="right" attach-width :min-width="220" :max-width="400">
            <slot>
				<chip v-if="chip" :color="valueColor" :icon="displayIcon" small>{{ displayValue !== null ? displayValue : missingText }}</chip>
				<template v-else-if="boolean">
					<template v-if="disabled">
						<Icon v-if="!displayValue" :icon="icons.inactive" color="warning" :center="center"/>
						<Icon v-else :icon="icons.active" color="success" :center="center"/>
					</template>
					<template v-else>
						<btn small icon>
							<Icon v-if="!displayValue" :icon="icons.inactive" color="warning" :center="center"/>
							<Icon v-else :icon="icons.active" color="success" :center="center"/>
						</btn>
					</template>
				</template>
				<lazy-dynamic-date v-else-if="type === 'date'" :date="displayValue" short disable-toggle :time="inputProps.time"/>
				<div v-else-if="type === 'color'" class="h-20 w-20 rounded" :style="{backgroundColor: displayValue ? displayValue : '#008200'}"/>
				<template v-else>
					<span v-if="displayValue !== null" :class="[valueColor ? 'text-' + valueColor : '', {'whitespace-no-wrap': type !== 'text'}]" :style="{color: colorHex}">{{ displayValue }}</span>
					<span v-else :class="[valueColor ? 'text-' + valueColor : '', {'whitespace-no-wrap': type !== 'text'}]">{{ missingText }}</span>
				</template>
			</slot>

            <template #menu>
				<div class="p-8" :class="{'mt-8': !!label}">
					<dynamic-form-input :enterkeyhint="allowsSubmitOnEnter ? 'done' : null" :type="type" :options="options" :input-props="inputProps" :rules="rules"
										:label="label"
										:placeholder="placeholder"
										:help="help"
										:center="center"
										:color="color"
										v-model:valid="isValid"
										v-model:error="errorMessage"
										:autofocus="autofocusInput" :clearable="clearable" inverted v-model="editValue" :name="name" @key-enter="handleKeyEnter"
										@key-esc="handleBlur" hide-details/>
				</div>

				<wrapper v-if="!selectSubmit" stretch>
					<btn text @click="handleBlur">{{ $t('action.cancel') }}</btn>
					<btn color="success" text @click="handleSave" :disabled="!isValid" :title="errorMessage">{{ $t('action.save') }}</btn>
				</wrapper>
			</template>
        </dropdown>
        <dynamic-form-input v-else :enterkeyhint="allowsSubmitOnEnter ? 'done' : null" :type="type" :options="options" :input-props="inputProps" :rules="rules"
                            :label="label"
                            :placeholder="placeholder"
                            :help="help"
                            :center="center"
                            v-model:valid="isValid"
                            v-model:error="errorMessage"
                            :clearable="clearable" inverted v-model="editValue" :name="name" @key-enter="handleKeyEnter"
                            @key-esc="handleBlur" hide-details>

            <template v-if="type === 'date'">
                <chip v-if="chip" :color="valueColor" :icon="displayIcon" small clickable>
                    <lazy-dynamic-date v-if="displayValue !== null" :date="displayValue" short disable-toggle :time="inputProps.time" class="flex h-full items-center"/>
                    <span v-else>{{ missingText }}</span>
                </chip>
                <lazy-dynamic-date v-else :date="displayValue" short disable-toggle :time="inputProps.time"/>
            </template>

        </dynamic-form-input>
    </div>
</template>

<script>
import Colorable from "../../../mixins/Colorable";
import {mdiCheck, mdiClose} from "@mdi/js";
import Positionable from "../../../mixins/Positionable";
import {toEuro, truncate} from "@spoferan/spoferan-ts-core";
import Validatable from "../../../mixins/Validatable";
import Inputtable from "../../../mixins/Inputtable";

export default {

    name: 'InlineFormInput',

    mixins: [Validatable, Colorable, Positionable, Inputtable],

	emits: ['blur', 'save'],

    props: {
        // The input component to render
        type: {
            type: String,
            default: 'input'
        },

        // The name of the input
        name: {
            type: String,
            default: null
        },

        // The label to show on the input
        label: {
            type: String,
            default: null
        },

		// The placeholder to show on the input
		placeholder: {
			type: String,
			default: null
		},

        // The help text to show within a help icon
        help: {
            type: String,
            default: null
        },

        // Whether to show clear button
        clearable: {
            type: Boolean,
            default: false
        },

        // States if the input shall be disabled
        disabled: {
            type: Boolean,
            default: false
        },

        // The validation rules
        rules: {
            type: Object,
            default: null
        },

        // Whether the value shall be displayed within a chip
        chip: {
            type: Boolean,
            default: false
        },

        // An icon to display on the chip
        icon: {
            type: String,
            default: null
        },

        // Whether to center the content
        center: {
            type: Boolean,
            default: false
        },

        // Whether the value shall be displayed as boolean icon
        boolean: {
            type: Boolean,
            default: false
        },

        // Whether a submit event shall be emitted as soon as a value get submitted
        // This prevents the 'save' and 'cancel' buttons to be displayed
        selectSubmit: {
            type: Boolean,
            default: false
        },

        // The options in case the type is 'select'
        options: {
            type: [Array, Number],
            default: () => {
                return []
            }
        },

        // The property on the options that defines a color to style the selected value
        colorProp:{
            type: String,
            default: null
        },

        // The specific props of the input component
        inputProps: {
            type: Object,
            default: () => {
                return {}
            }
        },

        // The text to display when the value is missing
        missingText:{
            type: String,
            default: '-'
        },

        // Whether to truncate the display text or the amount of characters after which to truncate
        truncate:{
            type: [String,Boolean, Number],
            default: null
        },
    },

    data() {
        return {
            errorMessage: null,
            editValue: this.modelValue,
            icons: {
                active: mdiCheck,
                inactive: mdiClose
            },
            // Whether the input is currently in editing mode
            isActive: false
        }
    },

    computed: {

        displayAsDropdown() {
            return this.type !== 'date';
        },

        autofocusInput() {
            return true;
        },

        // If the input type is one of the following, we allow a direct save when hitting enter
        allowsSubmitOnEnter() {
            return ['input', 'number', 'cents', 'date'].indexOf(this.type) > -1 && this.isValid;
        },

        persistent() {
            return this.editValue !== this.submitValue
        },

        valueColor() {
            if (this.color) {
                return this.color;
            }
            if (this.colorProp && this.options) {
                const option = this.options.find(option => option.value === this.submitValue);

                return option ? option[this.colorProp] : null;
            }

            return null;
        },

        displayValue() {
            let value = this.submitValue;
            if (this.type === 'select' && Array.isArray(this.options)) {
                if (value !== null) {
                    const option = this.options.find(option => option.value === value);
                    value = option ? option[this.inputProps.textProp ?? 'text'] : value
                } else if (Object.keys(this.inputProps).length && this.inputProps.unselect) {
                    value = this.inputProps.unselect;
                }

            } else if (this.type === 'cents') {
                value = toEuro(value);
            } else if (this.type === 'text') {
                if (this.truncate && this.modelValue) {
                    value = truncate(this.modelValue, [true, 'true', ''].includes(this.truncate) ? 100 : this.truncate);
                }
            }

            return value;
        },

        displayIcon() {
            if (this.type === 'select' && Array.isArray(this.options)) {
                if (this.submitValue) {
                    const option = this.options.find(option => option.value === this.submitValue);
                    return option && option.icon ? option.icon : this.icon
                }
            }

            return this.icon;
        }
    },

    watch: {
        isActive(active) {
            if (active) {
                this.editValue = this.modelValue
            }
        },

        editValue(value) {
            this.handleSelect();
        }
    },

    methods: {
        handleKeyEnter() {
            if (this.allowsSubmitOnEnter) {
                this.handleSave();
            } else if (!this.isValid && this.$refs.dropdown) {
                this.$refs.dropdown.shakeMenu(); // Show the user visually that the input is not valid
            }
        },

        /**
         * Handles the event when the user clicks out of the input.
         */
        handleBlur() {
            this.isActive = false;
            this.$emit('blur', this.submitValue)
        },

        handleSave() {
            this.submitValue = this.editValue;
            this.$emit('save', this.editValue)
            this.handleBlur();
        },

        handleSelect() {
            if (this.selectSubmit) {
                if (this.isValid) {
                    this.handleSave();
                } else {
                    this.$refs.dropdown.shakeMenu(); // Show the user visually that the input is not valid
                }
            }
        }
    }

}
</script>